import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router, type ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';

type GtagCommand = 'config' | 'event' | 'js';
type GtagParameters = Record<string, unknown>;

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (command: GtagCommand, target: string | Date, params?: GtagParameters) => void;
    }
}

@Injectable({
    providedIn: 'root'
})
export class GoogleAnalyticsService {
    private readonly router = inject(Router);
    private readonly document = inject(DOCUMENT);
    private readonly measurementId = environment.analytics?.googleAnalytics?.measurementId?.trim() ?? '';
    private readonly enabled = Boolean(environment.analytics?.googleAnalytics?.enabled && this.measurementId);

    private initialized = false;
    private lastPagePath: string | null = null;

    constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

    public initialize(): void {
        if (!this.enabled || this.initialized || !isPlatformBrowser(this.platformId)) {
            return;
        }

        this.initialized = true;
        this.installGtag();
        this.loadScript();
        this.sendPageView(this.getCanonicalPagePath());

        this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
            this.sendPageView(this.getCanonicalPagePath());
        });
    }

    public trackSafeEvent(eventName: string, params: GtagParameters = {}): void {
        if (!this.initialized || !window.gtag) {
            return;
        }

        window.gtag('event', eventName, params);
    }

    private installGtag(): void {
        window.dataLayer = window.dataLayer ?? [];
        window.gtag =
            window.gtag ??
            function gtag() {
                window.dataLayer?.push(arguments);
            };

        window.gtag('js', new Date());
        window.gtag('config', this.measurementId, {
            send_page_view: false
        });
    }

    private loadScript(): void {
        const scriptId = 'google-analytics-gtag';

        if (this.document.getElementById(scriptId)) {
            return;
        }

        const script = this.document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.measurementId)}`;
        this.document.head.appendChild(script);
    }

    private sendPageView(pagePath: string): void {
        if (!window.gtag || pagePath === this.lastPagePath) {
            return;
        }

        this.lastPagePath = pagePath;
        window.gtag('event', 'page_view', {
            page_title: this.getCanonicalPageTitle(),
            page_path: pagePath,
            page_location: `${this.document.location.origin}${pagePath}`
        });
    }

    private getCanonicalPagePath(): string {
        const pathSegments = this.getRoutePathSegments(this.router.routerState.snapshot.root);
        return pathSegments.length ? `/${pathSegments.join('/')}` : '/';
    }

    private getRoutePathSegments(route: ActivatedRouteSnapshot): string[] {
        const segments = route.routeConfig?.path?.split('/').filter((segment) => segment && segment !== '**') ?? [];

        const childSegments = route.firstChild ? this.getRoutePathSegments(route.firstChild) : [];
        return [...segments, ...childSegments];
    }

    private getCanonicalPageTitle(): string {
        let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
        let title = 'BettaAlpha';

        while (route) {
            if (typeof route.routeConfig?.title === 'string') {
                title = route.routeConfig.title;
            }

            route = route.firstChild ?? null;
        }

        return title;
    }
}
